import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import Navbar from "../../components/Navbar";
import NoteCard from "../../components/NoteCard";
import { publicRequest } from "../../requestMethods";
import NoteCardSkeleton from "../../components/NoteCardSkeleton";
import ProfileSkeleton from "../../components/ProfileSkeleton";
import AnalyticsDashboard from "../../components/AnalyticsDashboard";
import { follow, unFollow, updateProfile } from "../../redux/userRedux";
import theme from "../../theme";
import { mobile } from "../../responsive";
import { IconUser, IconPencil, IconCamera, IconX, IconCheck, IconDocument, IconFolder, IconUsers } from "../../icons";
import ActivityWidget from "../../components/ActivityWidget";
import OnlineDot from "../../components/OnlineDot";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import communityImg from "../../assets/community.png";
import ltceLogo from "../../assets/ltce.png";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Shell ── */
const Shell = styled.div`
  display: flex; min-height: 100vh; background: ${theme.bg};
`;

/* ── Sidebar ── */
const Sidebar = styled.aside`
  width: ${theme.sidebarWidth}; flex-shrink: 0; background: ${theme.sidebarBg};
  border-right: 1px solid ${theme.separator}; display: flex; flex-direction: column;
  position: fixed; top: 0; left: 0; height: 100vh; z-index: 60;
  ${mobile(`display: none;`)}
`;
const SidebarTop = styled.div`padding: 22px 18px 16px; border-bottom: 1px solid ${theme.separator};`;
const LogoRow = styled(Link)`display: flex; align-items: center; gap: 10px; text-decoration: none;`;
const LogoMark = styled.div`
  width: 36px; height: 36px; background: ${theme.primary}; color: #fff;
  border-radius: 9px; display: flex; align-items: center; justify-content: center;
  font-size: 17px; font-weight: 800; font-family: Georgia, serif; flex-shrink: 0;
`;
const LogoText = styled.div`font-size: 14px; font-weight: 700; color: ${theme.textMain}; line-height: 1.2;`;
const Nav = styled.nav`padding: 14px 10px 6px; display: flex; flex-direction: column; gap: 2px;`;
const SidebarMiddle = styled.div`
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  overflow: hidden; min-height: 0;
`;
const NavItem = styled(Link)`
  display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 500; color: ${theme.textMuted}; background: transparent;
  text-decoration: none; transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;
const CommunityMainNavItem = styled(NavItem)`justify-content: flex-start;`;
const CommunityChevron = styled.span`
  width: 7px;
  height: 7px;
  display: inline-block;
  border-right: 1.8px solid currentColor;
  border-bottom: 1.8px solid currentColor;
  transform: ${(p) => (p.$open ? "rotate(-135deg)" : "rotate(45deg)")};
  margin-top: ${(p) => (p.$open ? "2px" : "-1px")};
  transition: transform 0.15s ease, margin-top 0.15s ease;
`;
const CommunityChevronHit = styled.span`
  margin-left: auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: ${theme.primaryLight}; }
`;
const CommunitySubList = styled.div`
  margin: 4px 0 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const CommunitySubItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? "700" : "600")};
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;
const CommunitySubAvatar = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;
const SidebarUser = styled.div`padding: 14px 16px; border-top: 1px solid ${theme.separator}; display: flex; align-items: center; gap: 10px;`;
const SidebarAvatar = styled.div`
  width: 34px; height: 34px; border-radius: 50%; background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder}; overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: ${theme.primary}; cursor: pointer;
`;
const SidebarAvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const SidebarUserName = styled.div`font-size: 12.5px; font-weight: 700; color: ${theme.textMain}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const SidebarUserRole = styled.div`font-size: 10px; font-weight: 600; color: ${theme.textMuted}; text-transform: uppercase; letter-spacing: 0.06em;`;

/* ── Main ── */
const Main = styled.div`
  margin-left: ${theme.sidebarWidth}; flex: 1; display: flex; flex-direction: column; min-width: 0;
  ${mobile(`margin-left: 0;`)}
`;
const PageContent = styled.main`
  padding: 36px 40px 80px;
  ${mobile(`padding: 20px 16px 80px;`)}
`;

/* ── Profile header ── */
const ProfileHeader = styled.div`
  display: flex; align-items: flex-start; gap: 32px; margin-bottom: 36px;
  animation: ${fadeUp} 0.3s ease both;
  ${mobile(`flex-direction: column; gap: 20px;`)}
`;
const AvatarWrap = styled.div`position: relative; flex-shrink: 0;`;
const AvatarImg = styled.img`
  width: 130px; height: 130px; border-radius: 16px; object-fit: cover;
  border: 2px solid ${theme.cardBorder}; box-shadow: 0 4px 20px rgba(26,18,8,0.12);
`;
const AvatarPlaceholder = styled.div`
  width: 130px; height: 130px; border-radius: 16px; background: ${theme.bgAlt};
  border: 2px solid ${theme.cardBorder}; display: flex; align-items: center; justify-content: center; color: ${theme.textMuted};
`;
const AvatarEditBtn = styled.label`
  position: absolute; bottom: -6px; right: -6px; width: 28px; height: 28px; border-radius: 50%;
  background: ${theme.primary}; color: #fff; border: 2px solid ${theme.cardBg};
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  &:hover { background: ${theme.primaryDark}; }
`;
const HiddenInput = styled.input`display: none;`;
const ProfileMeta = styled.div`flex: 1; min-width: 0;`;
const RoleLabel = styled.div`font-size: 11px; font-weight: 700; color: ${theme.primary}; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 6px;`;
const ProfileNameRow = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;`;
const ProfileName = styled.h1`font-size: 32px; font-weight: 800; color: ${theme.textMain}; margin: 0; letter-spacing: -0.5px; line-height: 1.1;`;
const ProfileActions = styled.div`display: flex; gap: 10px; align-items: center; flex-shrink: 0;`;
const EditBtn = styled.button`
  background: ${theme.cardBg}; color: ${theme.textMain}; border: 1.5px solid ${theme.cardBorder};
  border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 600; font-family: inherit;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { border-color: ${theme.textMuted}; background: ${theme.bgAlt}; }
`;
const FollowBtn = styled.button`
  background: ${(p) => (p.$following ? theme.bgAlt : theme.primary)};
  color: ${(p) => (p.$following ? theme.textMain : "#fff")};
  border: 1.5px solid ${(p) => (p.$following ? theme.cardBorder : theme.primary)};
  border-radius: 8px; padding: 9px 24px; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { background: ${(p) => (p.$following ? theme.bgAlt : theme.primaryDark)}; transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const Institution = styled.div`font-size: 14px; color: ${theme.textMuted}; margin: 6px 0 12px;`;
const Bio = styled.p`font-size: 14px; color: ${theme.textSub}; line-height: 1.6; margin: 0 0 20px; max-width: 560px;`;
const StatsRow = styled.div`display: flex; gap: 32px; flex-wrap: wrap;`;
const StatBlock = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const StatValue = styled.div`font-size: 22px; font-weight: 800; color: ${theme.textMain}; line-height: 1;`;
const StatLabel = styled.div`font-size: 10px; font-weight: 700; color: ${theme.textMuted}; text-transform: uppercase; letter-spacing: 0.08em;`;

/* ── Tabs ── */
const TabRow = styled.div`display: flex; border-bottom: 2px solid ${theme.separator}; margin-bottom: 28px;`;
const Tab = styled.button`
  background: transparent; border: none; padding: 10px 20px 12px;
  font-size: 12px; font-weight: 700; font-family: inherit; cursor: pointer;
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  text-transform: uppercase; letter-spacing: 0.08em;
  border-bottom: 2px solid ${(p) => (p.$active ? theme.primary : "transparent")};
  margin-bottom: -2px; transition: color 0.15s;
  &:hover { color: ${theme.primary}; }
`;

/* ── Grid ── */
const NotesGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
  ${mobile(`grid-template-columns: 1fr;`)}
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatusText = styled.p`
  font-size: 14px; color: ${(p) => (p.$error ? theme.danger : theme.textMuted)}; text-align: center; padding: 40px 0;
`;

/* ── Edit modal ── */
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(26,18,8,0.45); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; z-index: 400; padding: 20px;
`;
const Modal = styled.div`
  background: ${theme.cardBg}; border-radius: 14px; border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 20px 60px rgba(26,18,8,0.18); padding: 28px; width: 100%; max-width: 420px;
  animation: ${fadeUp} 0.25s ease both;
`;
const ModalHeader = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 22px;`;
const ModalTitle = styled.h3`font-size: 16px; font-weight: 700; color: ${theme.textMain}; margin: 0;`;
const ModalClose = styled.button`
  background: transparent; border: 1.5px solid ${theme.cardBorder}; border-radius: 6px;
  padding: 4px 8px; cursor: pointer; color: ${theme.textMuted}; display: flex; align-items: center;
  &:hover { color: ${theme.textMain}; border-color: ${theme.textMuted}; }
`;
const ModalAvatarRow = styled.div`display: flex; justify-content: center; margin-bottom: 20px;`;
const ModalAvatar = styled.div`position: relative; width: 72px; height: 72px; border-radius: 50%; overflow: hidden; border: 2px solid ${theme.cardBorder};`;
const ModalAvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const ModalAvatarOverlay = styled.label`
  position: absolute; inset: 0; background: rgba(26,18,8,0.48); display: flex; align-items: center;
  justify-content: center; color: #fff; cursor: pointer; opacity: 0; transition: opacity 0.18s; border-radius: 50%;
  ${ModalAvatar}:hover & { opacity: 1; }
`;
const FieldGroup = styled.div`margin-bottom: 14px;`;
const Label = styled.label`display: block; font-size: 11px; font-weight: 700; color: ${theme.textMuted}; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.8px;`;
const Input = styled.input`
  width: 100%; background: ${theme.bg}; border: 1.5px solid ${theme.cardBorder}; border-radius: 7px;
  padding: 9px 12px; font-size: 13.5px; font-family: inherit; color: ${theme.textMain}; outline: none; box-sizing: border-box;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;
const Textarea = styled.textarea`
  width: 100%; background: ${theme.bg}; border: 1.5px solid ${theme.cardBorder}; border-radius: 7px;
  padding: 9px 12px; font-size: 13.5px; font-family: inherit; color: ${theme.textMain}; outline: none;
  resize: vertical; min-height: 72px; box-sizing: border-box;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;
const ModalActions = styled.div`display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;`;
const CancelBtn = styled.button`
  background: transparent; border: 1.5px solid ${theme.cardBorder}; border-radius: 7px;
  padding: 9px 20px; font-size: 13px; font-weight: 600; font-family: inherit; color: ${theme.textMuted}; cursor: pointer;
  &:hover { border-color: ${theme.textMuted}; }
`;
const SaveBtn = styled.button`
  background: ${theme.primary}; color: #fff; border: none; border-radius: 7px;
  padding: 9px 24px; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  &:hover:not(:disabled) { background: ${theme.primaryDark}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const SaveError = styled.p`font-size: 12.5px; color: ${theme.danger}; margin: 10px 0 0; text-align: center;`;

export default function Profile() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((s) => s.user.currentUser);

  const [profileUser, setProfileUser] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [userError, setUserError] = useState(null);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("artifacts");
  const [commentCounts, setCommentCounts] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editInstitution, setEditInstitution] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editAvatar, setEditAvatar] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [communityMenuOpen, setCommunityMenuOpen] = useState(false);
  const modalAvatarInputRef = useRef(null);
  const quickAvatarRef = useRef(null);

  const STATIC = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");
  const isOwnProfile = currentUser?._id === userId;
  const isFollowing = currentUser?.followings?.includes(userId);
  const avatarSrc = profileUser?.profilePicture ? `${STATIC}/images/${profileUser.profilePicture}` : null;
  const currentUserAvatarSrc = currentUser?.profilePicture ? `${STATIC}/images/${currentUser.profilePicture}` : null;

  const onlineSet = useOnlineStatus(currentUser?._id, userId ? [userId] : []);
  const isProfileOnline = onlineSet.has(userId);

  useEffect(() => {
    let cancelled = false;
    setLoadingUser(true);
    publicRequest.get(`/users/${userId}`)
      .then((res) => { if (!cancelled) setProfileUser(res.data); })
      .catch(() => { if (!cancelled) setUserError("Failed to load profile."); })
      .finally(() => { if (!cancelled) setLoadingUser(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingNotes(true);
    publicRequest.get(`/notes/profile/${userId}`)
      .then((res) => { if (!cancelled) setNotes(res.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingNotes(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const counts = {};
      try {
        await Promise.all(
          notes.map(async (note) => {
            try {
              const res = await publicRequest.get(`/comments/${note._id}`);
              counts[note._id] = res.data.length;
            } catch {
              counts[note._id] = 0;
            }
          })
        );
      } finally {
        if (!cancelled) setCommentCounts(counts);
      }
    };

    if (notes.length === 0) {
      setCommentCounts({});
      return () => { cancelled = true; };
    }

    run();
    return () => { cancelled = true; };
  }, [notes]);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await publicRequest.get("/community");
        setCommunities(r.data || []);
      } catch {
        setCommunities([]);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!currentUser?.institution) return;
    publicRequest.get(`/community/institution/${encodeURIComponent(currentUser.institution)}`)
      .then((r) => {
        setMyCommunity(r.data);
        setCommunities((prev) => prev.find((c) => c._id === r.data._id) ? prev : [r.data, ...prev]);
      })
      .catch(() => {});
  }, [currentUser]);

  const isMember = (community) => community?.members?.some((m) => m === currentUser?._id || m?._id === currentUser?._id || m?.toString() === currentUser?._id);
  const isLTCECommunity = (community) => {
    const target = `${community?.name || ""} ${community?.institution || ""}`.toUpperCase();
    return target.includes("LTCE");
  };
  const getCommunityLogo = (community) => isLTCECommunity(community) ? ltceLogo : communityImg;
  const communityNavList = (() => {
    const items = [];
    if (myCommunity) items.push(myCommunity);
    communities.forEach((c) => {
      if (isMember(c) && !items.find((x) => x._id === c._id)) items.push(c);
    });
    return items;
  })();

  const openEdit = () => {
    setEditUsername(profileUser?.username || "");
    setEditInstitution(profileUser?.institution || "");
    setEditBio(profileUser?.bio || "");
    setEditAvatar(null);
    setEditAvatarPreview(avatarSrc);
    setSaveError(null);
    setEditOpen(true);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditAvatar(file);
    setEditAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!editUsername.trim()) { setSaveError("Username cannot be empty."); return; }
    setSaving(true); setSaveError(null);
    try {
      const fd = new FormData();
      fd.append("userId", currentUser._id);
      fd.append("username", editUsername.trim());
      fd.append("institution", editInstitution.trim());
      fd.append("bio", editBio.trim());
      if (editAvatar) fd.append("profilePicture", editAvatar);
      const res = await publicRequest.put(`/users/${currentUser._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfileUser(res.data);
      dispatch(updateProfile(res.data));
      setEditOpen(false);
    } catch { setSaveError("Failed to save changes."); }
    finally { setSaving(false); }
  };

  const handleFollow = async () => {
    if (!currentUser || followLoading) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await publicRequest.put(`/users/${userId}/unfollow`, { userId: currentUser._id });
        dispatch(unFollow(userId));
        setProfileUser((p) => p ? { ...p, followers: p.followers.filter((id) => id !== currentUser._id) } : p);
      } else {
        await publicRequest.put(`/users/${userId}/follow`, { userId: currentUser._id });
        dispatch(follow(userId));
        setProfileUser((p) => p ? { ...p, followers: [...p.followers, currentUser._id] } : p);
      }
    } catch {} finally { setFollowLoading(false); }
  };

  const avgRating = (() => {
    const rated = notes.filter((n) => n.ratings?.length > 0);
    if (!rated.length) return null;
    const total = rated.reduce((s, n) => s + n.ratings.reduce((a, r) => a + r.value, 0) / n.ratings.length, 0);
    return (total / rated.length).toFixed(1);
  })();

  return (
    <Shell>
      {/* Sidebar */}
      <Sidebar>
        <SidebarTop>
          <LogoRow to="/">
            <LogoMark>N</LogoMark>
            <div><LogoText>NotesHub</LogoText></div>
          </LogoRow>
        </SidebarTop>
        <Nav>
          <NavItem to="/"><IconDocument size="15px" /> Feed</NavItem>
          <NavItem to="/folders"><IconFolder size="15px" /> Folders</NavItem>
          <CommunityMainNavItem to="/community"><IconUsers size="15px" /> Community
            <CommunityChevronHit
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommunityMenuOpen((v) => !v);
              }}
              aria-label="Toggle my communities"
              title="Toggle my communities"
            >
              <CommunityChevron $open={communityMenuOpen} />
            </CommunityChevronHit>
          </CommunityMainNavItem>
          {communityMenuOpen && (
            <CommunitySubList>
              {communityNavList.length === 0 ? (
                <CommunitySubItem type="button" disabled style={{ opacity: 0.6, cursor: "default" }}>
                  No communities yet
                </CommunitySubItem>
              ) : communityNavList.map((c) => (
                <CommunitySubItem
                  key={c._id}
                  type="button"
                  onClick={() => navigate("/community")}
                >
                  <CommunitySubAvatar src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                  {c.name}
                </CommunitySubItem>
              ))}
            </CommunitySubList>
          )}
        </Nav>
        <SidebarMiddle>
          <ActivityWidget />
        </SidebarMiddle>
        {currentUser && (
          <SidebarUser>
            <SidebarAvatar onClick={() => navigate(`/profile/${currentUser._id}`)}>
              {currentUserAvatarSrc
                ? <SidebarAvatarImg src={currentUserAvatarSrc} alt={currentUser.username} />
                : currentUser.username?.[0]?.toUpperCase()}
            </SidebarAvatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SidebarUserName>{currentUser.username}</SidebarUserName>
              <SidebarUserRole>Student</SidebarUserRole>
            </div>
          </SidebarUser>
        )}
      </Sidebar>

      {/* Main */}
      <Main>
        <Navbar />
        <PageContent>
          {loadingUser ? (
            <>
              <ProfileSkeleton />
              <NotesGrid>
                {Array.from({ length: 6 }).map((_, i) => <NoteCardSkeleton key={i} />)}
              </NotesGrid>
            </>
          ) : userError ? (
            <StatusText $error>{userError}</StatusText>
          ) : profileUser ? (
            <>
              <ProfileHeader>
                <AvatarWrap>
                  {avatarSrc
                    ? <AvatarImg src={avatarSrc} alt={profileUser.username} />
                    : <AvatarPlaceholder><IconUser size="44px" /></AvatarPlaceholder>}
                  <OnlineDot online={isProfileOnline} size="14px" borderColor={theme.bg} style={isOwnProfile ? { top: 4, left: 4, bottom: "auto", right: "auto" } : {}} />
                  {isOwnProfile && (
                    <>
                      <AvatarEditBtn htmlFor="quick-avatar" title="Change photo">
                        <IconCamera size="13px" />
                      </AvatarEditBtn>
                      <HiddenInput id="quick-avatar" ref={quickAvatarRef} type="file" accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          openEdit();
                          setEditAvatar(f);
                          setEditAvatarPreview(URL.createObjectURL(f));
                        }}
                      />
                    </>
                  )}
                </AvatarWrap>

                <ProfileMeta>
                  <RoleLabel>Student</RoleLabel>
                  <ProfileNameRow>
                    <ProfileName>{profileUser.username}</ProfileName>
                    <ProfileActions>
                      {isOwnProfile ? (
                        <EditBtn onClick={openEdit}>
                          <IconPencil size="12px" /> Edit Profile
                        </EditBtn>
                      ) : (
                        <FollowBtn $following={isFollowing} onClick={handleFollow} disabled={followLoading}>
                          {followLoading ? "…" : isFollowing ? "Unfollow" : "Follow"}
                        </FollowBtn>
                      )}
                    </ProfileActions>
                  </ProfileNameRow>

                  {profileUser.institution && <Institution>{profileUser.institution}</Institution>}
                  {profileUser.bio && <Bio>{profileUser.bio}</Bio>}

                  <StatsRow>
                    <StatBlock>
                      <StatValue>{notes.length}</StatValue>
                      <StatLabel>Notes</StatLabel>
                    </StatBlock>
                    <StatBlock>
                      <StatValue>{profileUser.followers?.length ?? 0}</StatValue>
                      <StatLabel>Followers</StatLabel>
                    </StatBlock>
                    <StatBlock>
                      <StatValue>{profileUser.followings?.length ?? 0}</StatValue>
                      <StatLabel>Following</StatLabel>
                    </StatBlock>
                    {avgRating && (
                      <StatBlock>
                        <StatValue>
                          {avgRating} <span style={{ color: theme.primary, fontSize: 18 }}>★</span>
                        </StatValue>
                        <StatLabel>Avg Rating</StatLabel>
                      </StatBlock>
                    )}
                  </StatsRow>
                </ProfileMeta>
              </ProfileHeader>

              <TabRow>
                <Tab $active={activeTab === "artifacts"} onClick={() => setActiveTab("artifacts")}>
                  My Notes
                </Tab>
                <Tab $active={activeTab === "stats"} onClick={() => setActiveTab("stats")}>
                  Stats
                </Tab>
              </TabRow>

              {activeTab === "stats" ? (
                <AnalyticsDashboard currentUser={profileUser} myNotes={notes} commentCounts={commentCounts} />
              ) : loadingNotes ? (
                <NotesGrid>
                  {Array.from({ length: 6 }).map((_, i) => <NoteCardSkeleton key={i} />)}
                </NotesGrid>
              ) : notes.length === 0 ? (
                <StatusText>No notes uploaded yet.</StatusText>
              ) : (
                <NotesGrid>
                  {notes.map((note) => (
                    <NoteCard key={note._id} note={note} />
                  ))}
                </NotesGrid>
              )}
            </>
          ) : null}
        </PageContent>
      </Main>

      {/* Edit modal */}
      {editOpen && (
        <Overlay onClick={() => setEditOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Profile</ModalTitle>
              <ModalClose onClick={() => setEditOpen(false)}><IconX size="13px" /></ModalClose>
            </ModalHeader>

            <ModalAvatarRow>
              <ModalAvatar>
                {editAvatarPreview
                  ? <ModalAvatarImg src={editAvatarPreview} alt="Preview" />
                  : <div style={{ width: "100%", height: "100%", background: theme.bgAlt, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textMuted }}>
                      <IconUser size="28px" />
                    </div>}
                <ModalAvatarOverlay htmlFor="modal-avatar-upload">
                  <IconCamera size="16px" />
                </ModalAvatarOverlay>
                <HiddenInput id="modal-avatar-upload" ref={modalAvatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} />
              </ModalAvatar>
            </ModalAvatarRow>

            <FieldGroup>
              <Label htmlFor="edit-username">Username</Label>
              <Input id="edit-username" value={editUsername} onChange={(e) => setEditUsername(e.target.value)} placeholder="Your username" autoFocus />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="edit-institution">Institution</Label>
              <Input id="edit-institution" value={editInstitution} onChange={(e) => setEditInstitution(e.target.value)} placeholder="University or school" />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea id="edit-bio" value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder="A short bio…" />
            </FieldGroup>

            {saveError && <SaveError>{saveError}</SaveError>}

            <ModalActions>
              <CancelBtn onClick={() => setEditOpen(false)}>Cancel</CancelBtn>
              <SaveBtn onClick={handleSave} disabled={saving || !editUsername.trim()}>
                <IconCheck size="13px" />
                {saving ? "Saving…" : "Save Changes"}
              </SaveBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Shell>
  );
}
